/**
 * LE REGISTRE DES SOURCES — ce que la carte doit AVOUER.
 *
 * ⚠️ IL EST DANS `src/partage/` POUR LA MÊME RAISON QUE `rubriques.ts` :
 * l'étage `interface` du Dockerfile ne copie que `frontend/` et `src/partage/`.
 * Un registre rangé dans `frontend/src/` compilerait dans l'interface et
 * laisserait le SERVICE incapable de garder ce qu'il sert.
 *
 * ⚠️⚠️ CE N'EST PAS DE LA DOCUMENTATION, C'EST UNE OBLIGATION CONTRACTUELLE.
 * La licence Copernicus (art. 6) impose des textes LITTÉRAUX, et son art. 9
 * fait perdre IMMÉDIATEMENT la totalité des droits accordés en cas de
 * manquement. L'ODbL impose l'attribution et le maintien de la licence sur
 * toute base dérivée. Une attribution due et jamais affichée n'est pas une
 * négligence de forme : c'est une exploitation sans titre.
 *
 * ⚠️⚠️⚠️ ÉTAT TROUVÉ LE 18/09/2026 : L'INTERFACE N'EN AFFICHAIT AUCUNE.
 * `carte.tsx` posait `attributionControl: false`, et un `grep` de `©`,
 * `OpenStreetMap` et `ODbL` sur l'ENSEMBLE de `frontend/src/*.tsx` rendait
 * **zéro**. Le mot « attribution » s'y trouvait bien — mais au sens foncier
 * guinéen (« attribution coutumière »), un homonyme qui faisait passer la
 * recherche pour concluante.
 *
 * ── CE QUI EST MESURÉ, ET NON SUPPOSÉ ────────────────────────────────────
 * La provenance ne se juge pas : chaque table porte sa colonne `source`, et
 * c'est elle qui décide. Mesuré en production le 18/09/2026 :
 *
 *   ter.division   462 / 462             `mowibo_maps.limites_osm_<n>#<id>`
 *   cad.batiment   1 744 535 / 1 744 535 `mowibo_maps.maps_batiments#<id>`
 *   ter.voie       219 150               copie datée du réseau viaire de Maps
 *   cad.parcelle   988 688 / 988 695     `cad.mailler_division` — NOTRE maillage
 *
 * ⚠️ LE CAS DES PARCELLES EST LE PIÈGE. Elles sont produites par nous, et
 * pourtant elles ne sont pas libres de toute attribution : le maillage est
 * calculé À L'INTÉRIEUR des limites de `ter.division`, qui sont OSM. Une base
 * calculée depuis une base ODbL est une base DÉRIVÉE. Déclarer les parcelles
 * « natives » parce que notre code les a écrites serait exact sur l'auteur et
 * faux sur la source.
 *
 * ── CE QUI EST VRAIMENT NATIF ─────────────────────────────────────────────
 * Les actes, droits, titres, titulaires, dossiers, bornes et levés : produits
 * par le registre lui-même, aucune attribution tierce due. C'est là qu'est la
 * valeur du produit, et c'est précisément ce que la carte ne montre pas.
 */

export interface SourceDonnees {
  cle: string;
  libelle: string;
  /** Nom court de la licence, pour l'agent pressé. */
  licence: string;
  /**
   * ⚠️ TEXTE LITTÉRAL, JAMAIS PARAPHRASÉ. Les licences imposent la formule,
   * pas son idée. Une reformulation « plus claire » est un manquement.
   */
  mention: string;
  /** Formule imposée quand les données ont été adaptées ou modifiées. */
  mentionAdaptee?: string;
  /** Clause de non-responsabilité que la licence oblige à transmettre. */
  responsabilite?: string;
  url?: string;
  /** Les couches de la carte que cette source alimente. */
  couches: string[];
  /** Comment la provenance a été établie — la preuve, pas l'opinion. */
  preuve: string;
}

/**
 * ⚠️ TOUTE COUCHE SERVIE PAR `modules/tuiles.ts` DOIT APPARAÎTRE ICI.
 * Le garde vit dans `tests/sources-attribution.test.ts` : il lit les clés de
 * `ZOOM_MINIMAL` et exige qu'aucune ne soit orpheline. Ajouter une couche sans
 * sa source fait rougir la suite — c'est le seul moyen qu'une attribution ne
 * s'oublie pas, puisque rien à l'écran ne signale son absence.
 */
export const SOURCES: SourceDonnees[] = [
  {
    cle: 'osm',
    libelle: 'OpenStreetMap, via Mowibo Maps',
    licence: 'ODbL 1.0',
    mention: '© les contributeurs d’OpenStreetMap — ODbL',
    mentionAdaptee:
      'Contient des données d’OpenStreetMap © les contributeurs d’OpenStreetMap, sous licence ODbL 1.0.',
    url: 'https://www.openstreetmap.org/copyright',
    couches: ['divisions', 'divisions_noms', 'collectivites', 'collectivites_noms',
              'rues', 'sections', 'sections_noms', 'batiments', 'parcelles'],
    preuve:
      'ter.division 462/462 et cad.batiment 1 744 535/1 744 535 portent une `source` ' +
      '`mowibo_maps.*` ; ter.voie est une copie datée du réseau viaire de Maps ; ' +
      'cad.parcelle est notre maillage, mais calculé dans des limites OSM — base dérivée. '  +
      'cad.section l’est doublement : 152 442 sections sur 153 248 sont tracées au '        +
      'carroyage DANS des limites OSM, et les 806 autres sont bornées par ter.voie, '       +
      'copie du réseau viaire de Maps (mesuré le 19/09/2026, group by origine). '           +
      '⚠️ `collectivites` (migration 210) N’Y ÉCHAPPE PAS, et c’est le piège : ses '       +
      'contours viennent de ter.circonscription, une VUE sur la table étrangère '           +
      'ter.maps_regions_distant (serveur `maps`) — donc d’OSM, malgré un nom qui '          +
      'annonce un registre d’État. Matérialiser une source ne change pas sa licence.',
  },
  {
    cle: 'copernicus-dem-glo30',
    libelle: 'Copernicus DEM GLO-30',
    licence: 'COP-DEM-GLO-30-F — Free & Open',
    mention:
      '© DLR e.V. 2010-2014 and © Airbus Defence and Space GmbH 2014-2018 provided under ' +
      'COPERNICUS by the European Union and ESA; all rights reserved.',
    mentionAdaptee:
      'produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and ' +
      'Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved',
    responsabilite:
      'The organisations in charge of the Copernicus programme by law or by delegation do not ' +
      'incur any liability for any use of the Copernicus WorldDEM-30.',
    couches: ['relief'],
    preuve:
      'EULA livré avec chaque tuile (INFO/eula_F.pdf), lu le 18/09/2026 : art. 3 mondial et ' +
      'sans limite de durée, art. 4 adaptation autorisée, art. 5 gratuit, aucune clause non ' +
      'commerciale (grep → 0, témoin positif « licence » → 19). Art. 9 : tout manquement résilie.',
  },
];

/** Les sources qui alimentent une couche donnée. */
export const sourcesDeLaCouche = (couche: string): SourceDonnees[] =>
  SOURCES.filter((s) => s.couches.includes(couche));

/**
 * La ligne d'attribution à afficher pour un ensemble de couches présentes.
 *
 * ⚠️ ELLE SE CALCULE, ELLE NE SE RÉCITE PAS. Une chaîne écrite en dur dans
 * l'interface diverge dès qu'une couche change de source — et la divergence
 * prend la pire forme possible : une attribution qui a l'air juste et crédite
 * la mauvaise base.
 *
 * ⚠️ ON REND LA MENTION **ADAPTÉE** dès qu'elle existe. Cadastral ne rediffuse
 * jamais la donnée telle quelle : elle est reprojetée, découpée en tuiles,
 * filtrée par habilitation. C'est la définition même de l'adaptation.
 */
export function attribution(couchesPresentes: string[]): string[] {
  const vues = new Set<string>();
  const lignes: string[] = [];
  for (const couche of couchesPresentes) {
    for (const s of sourcesDeLaCouche(couche)) {
      if (vues.has(s.cle)) continue;
      vues.add(s.cle);
      lignes.push(s.mentionAdaptee ?? s.mention);
    }
  }
  return lignes;
}
